import type { MenstrualHealthState } from "./MenstrualHealthContext";
import { deriveCycleLengthRange } from "./deriveCycleLengthRange";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

export function transformMenstrualHealthStateToRequest(
  state: MenstrualHealthState,
): PatchPayload {
  type SymptomEntry = {
    id?: string;
    symptom: "cramping" | "pelvic_pain" | "constipation" | "diarrhea" | "other";
    other?: string | null;
    op?: "delete";
  };

  const symptomsPayload: SymptomEntry[] = [];

  // Add deletions - use a dummy symptom value since the API ignores it for deletes
  state.symptomIdsToDelete.forEach((id) => {
    if (id) {
      symptomsPayload.push({ id, symptom: "other", op: "delete" });
    }
  });

  // Add/update symptoms
  if (state.hasPeriodSymptoms === true) {
    const deleteIdSet = new Set(state.symptomIdsToDelete);
    state.symptoms
      .filter((s) => !s.id || !deleteIdSet.has(s.id))
      .forEach((s) => {
        const entry: SymptomEntry = {
          symptom: s.symptom,
        };
        if (s.id) entry.id = s.id;
        if (s.other != null) entry.other = s.other;
        symptomsPayload.push(entry);
      });
  } else if (state.hasPeriodSymptoms === false) {
    // Delete all existing symptoms when user says "none"
    const deleteIdSet = new Set(state.symptomIdsToDelete);
    state.symptoms.forEach((s) => {
      if (s.id && !deleteIdSet.has(s.id)) {
        symptomsPayload.push({ id: s.id, symptom: s.symptom, op: "delete" });
      }
    });
  }

  const menstrualHealth: NonNullable<
    NonNullable<PatchPayload["obGynHistory"]>["menstrualHealth"]
  > = {};
  const inferredCycleLength = deriveCycleLengthRange([
    state.period1StartDate,
    state.period2StartDate,
    state.period3StartDate,
  ]);

  if (state.id) menstrualHealth.id = state.id;
  if (state.cycleStatus !== undefined) {
    menstrualHealth.cycleStatus = state.cycleStatus;
  }
  if (state.ageAtFirstPeriod !== undefined) {
    menstrualHealth.ageAtFirstPeriod = state.ageAtFirstPeriod;
  }
  if (inferredCycleLength) {
    menstrualHealth.minAverageCycleLength =
      inferredCycleLength.minAverageCycleLength;
    menstrualHealth.maxAverageCycleLength =
      inferredCycleLength.maxAverageCycleLength;
  } else {
    if (state.minAverageCycleLength !== undefined) {
      menstrualHealth.minAverageCycleLength = state.minAverageCycleLength;
    }
    if (state.maxAverageCycleLength !== undefined) {
      menstrualHealth.maxAverageCycleLength = state.maxAverageCycleLength;
    }
  }
  if (state.menstrualFlowIntensity !== undefined) {
    menstrualHealth.menstrualFlowIntensity = state.menstrualFlowIntensity;
  }
  if (state.period1StartDate !== undefined) {
    menstrualHealth.period1StartDate = state.period1StartDate;
  }
  if (state.period1EndDate !== undefined) {
    menstrualHealth.period1EndDate = state.period1EndDate;
  }
  if (state.period2StartDate !== undefined) {
    menstrualHealth.period2StartDate = state.period2StartDate;
  }
  if (state.period2EndDate !== undefined) {
    menstrualHealth.period2EndDate = state.period2EndDate;
  }
  if (state.period3StartDate !== undefined) {
    menstrualHealth.period3StartDate = state.period3StartDate;
  }
  if (state.period3EndDate !== undefined) {
    menstrualHealth.period3EndDate = state.period3EndDate;
  }
  if (state.bleedsOutsidePeriod !== undefined) {
    menstrualHealth.bleedsOutsidePeriod = state.bleedsOutsidePeriod;
  }
  if (state.painScale !== undefined) {
    menstrualHealth.painScale = state.painScale;
  }
  if (state.takesPainMedication !== undefined) {
    menstrualHealth.takesPainMedication = state.takesPainMedication;
  }
  if (state.hasPeriodSymptoms !== undefined) {
    menstrualHealth.hasPeriodSymptoms = state.hasPeriodSymptoms;
  }
  if (state.awareOfFertileWindow !== undefined) {
    menstrualHealth.awareOfFertileWindow = state.awareOfFertileWindow;
  }

  if (symptomsPayload.length > 0) {
    menstrualHealth.symptoms = symptomsPayload;
  }

  if (Object.keys(menstrualHealth).length === 0) {
    return {};
  }

  return {
    obGynHistory: {
      menstrualHealth,
    },
  };
}
